import { addDays, endOfMonth, getDay, startOfMonth } from "date-fns";
import { RefreshCwIcon } from "lucide-react";
import { useMemo } from "react";
import {
  formatDateForInput,
  formatDateTimeLocal,
} from "@/shared/lib/date-time";
import {
  countByDate,
  countByDateAndType,
  isDayOff,
  toYMD,
} from "./timetable-utils";
import type { TimetableItem, WorkCalendarDayOff } from "./types";

type PublicTimetableMonthViewProps = {
  monthDate: Date;
  items: TimetableItem[];
  onDayClick: (date: string) => void;
  /** ວັນພັກ/ວັນພິເສດ (mgmt_work_calendar) ເພື່ອສະແດງໃນປະຕິທິນ */
  dayOffs?: WorkCalendarDayOff[];
};

const WEEKDAY_LABELS = [
  "ວັນອາທິດ",
  "ວັນຈັນ",
  "ວັນອັງຄານ",
  "ວັນພຸດ",
  "ວັນພະຫັດ",
  "ວັນສຸກ",
  "ວັນເສົາ",
];

export function PublicTimetableMonthView({
  monthDate,
  items,
  onDayClick,
  dayOffs = [],
}: PublicTimetableMonthViewProps) {
  const countByDay = useMemo(() => countByDate(items), [items]);
  const countByDayAndType = useMemo(() => countByDateAndType(items), [items]);
  const todayYMD = formatDateForInput(new Date());
  const totalSessions = useMemo(
    () => Array.from(countByDay.values()).reduce((a, b) => a + b, 0),
    [countByDay],
  );
  const calendarDays = useMemo(() => {
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    const firstDow = getDay(start);
    const cells: { date: Date; isCurrentMonth: boolean }[] = [];
    const pad = firstDow;
    for (let i = 0; i < pad; i++) {
      cells.push({ date: addDays(start, i - pad), isCurrentMonth: false });
    }
    const daysInMonth = end.getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), d);
      cells.push({ date, isCurrentMonth: true });
    }
    const remaining = 42 - cells.length;
    for (let i = 0; i < remaining; i++) {
      cells.push({
        date: addDays(end, i + 1),
        isCurrentMonth: false,
      });
    }
    return cells;
  }, [monthDate]);

  return (
    <div className="flex flex-col gap-3">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {WEEKDAY_LABELS.map((label) => (
              <th
                key={label}
                className="border-border border-r border-b bg-muted/50 p-2 text-center font-medium text-muted-foreground text-xs last:border-r-0"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, row) => (
            <tr
              key={calendarDays
                .slice(row * 7, row * 7 + 7)
                .map((c) => toYMD(c.date))
                .join(",")}
            >
              {calendarDays
                .slice(row * 7, row * 7 + 7)
                .map(({ date, isCurrentMonth }) => {
                  const dateStr = toYMD(date);
                  const counts = countByDayAndType.get(dateStr) ?? {
                    class: 0,
                    private: 0,
                    room_booking: 0,
                    event: 0,
                    project_task: 0,
                    meeting_action: 0,
                  };
                  const off = isDayOff(dateStr, dayOffs);
                  const isHoliday = off?.type === "holiday";
                  const isClickable = isCurrentMonth;
                  const isToday = dateStr === todayYMD;
                  const hasAny =
                    counts.class +
                      counts.private +
                      counts.room_booking +
                      counts.event +
                      counts.project_task +
                      counts.meeting_action >
                    0;
                  return (
                    <td
                      key={dateStr}
                      className={`min-h-[80px] w-[140px] border-border border-r border-b p-2 align-top last:border-r-0 ${
                        isCurrentMonth
                          ? "bg-background"
                          : "bg-muted/30 text-muted-foreground"
                      } ${isToday ? "border-destructive ring-1 ring-destructive" : ""} ${isHoliday ? "bg-destructive/5" : ""} ${isClickable ? "cursor-pointer hover:bg-muted/50" : ""}`}
                      onClick={() => isClickable && onDayClick(dateStr)}
                      onKeyDown={(e) => {
                        if (
                          isClickable &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          e.preventDefault();
                          onDayClick(dateStr);
                        }
                      }}
                      role={isClickable ? "button" : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                    >
                      <div className="flex h-full min-h-[64px] flex-col">
                        <div className="flex items-center gap-1">
                          <span
                            className={`font-medium ${isToday ? "text-destructive" : ""}`}
                          >
                            {date.getDate()}
                          </span>
                          {isToday && (
                            <span className="size-1.5 shrink-0 rounded-full bg-destructive" />
                          )}
                        </div>
                        {isCurrentMonth && (hasAny || off) && (
                          <div className="mt-auto flex flex-col items-center justify-center gap-1 pt-2">
                            {isToday && (
                              <span className="inline-flex items-center rounded-full border border-destructive bg-destructive/10 px-2 py-0.5 font-semibold text-destructive text-xs">
                                ມື້ນີ້
                              </span>
                            )}
                            {off && (
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium text-xs ${off.type === "holiday" ? "bg-destructive/20 text-destructive" : "bg-muted text-muted-foreground"}`}
                                title={off.description ?? undefined}
                              >
                                {off.type === "holiday" ? "ວັນພັກ" : "ພິເສດ"}
                              </span>
                            )}
                            {counts.class > 0 && (
                              <span
                                className={`inline-flex items-center rounded-full bg-primary px-2 py-0.5 font-medium text-primary-foreground text-xs ${isToday ? "font-semibold" : ""}`}
                              >
                                {counts.class} ຄລາດສ໌
                              </span>
                            )}
                            {counts.private > 0 && (
                              <span className="inline-flex items-center rounded-full border border-muted-foreground/60 bg-muted-foreground/20 px-2 py-0.5 font-medium text-muted-foreground text-xs">
                                {counts.private} ວິຊາສ່ວນຕົວ
                              </span>
                            )}
                            {counts.room_booking > 0 && (
                              <span className="inline-flex items-center rounded-full border border-border bg-muted/60 px-2 py-0.5 font-medium text-foreground text-xs">
                                {counts.room_booking} ຈອງຫ້ອງ
                              </span>
                            )}
                            {counts.event > 0 && (
                              <span className="inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 font-medium text-primary text-xs">
                                {counts.event} ກິດຈະກຳ
                              </span>
                            )}
                            {counts.project_task > 0 && (
                              <span className="inline-flex items-center rounded-full bg-amber-500/20 px-2 py-0.5 font-medium text-amber-700 text-xs dark:text-amber-400">
                                {counts.project_task} ວັນສົ່ງ Task
                              </span>
                            )}
                            {counts.meeting_action > 0 && (
                              <span className="inline-flex items-center rounded-full bg-blue-500/20 px-2 py-0.5 font-medium text-blue-700 text-xs dark:text-blue-400">
                                {counts.meeting_action} ປະເດັນ
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-wrap items-center justify-between gap-2 border-border border-t pt-3 text-muted-foreground text-xs">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="size-2 shrink-0 rounded-full bg-primary" />
            {totalSessions} ລວມເຊດຊັນໃນເດືອນນີ້
          </span>
        </div>
        <span className="flex items-center gap-1.5">
          <RefreshCwIcon className="size-3.5" />
          ອັບເດດລ່າສຸດ: {formatDateTimeLocal(new Date())}
        </span>
      </div>
    </div>
  );
}
